/**
 * The SSE event union (ARCHITECTURE §10.2, ADR-006, graft G-14).
 *
 * These are the `{type, payload}` events the store-subscription bridge
 * publishes from each project runtime (fed by the 9 Zustand stores,
 * rustd's watcher SSE and runnerd's run SSE), fanned out by the gateway
 * on `GET /api/v1/projects/{id}/events` and replayed from the
 * short-retention `events` ring via `Last-Event-ID`.
 *
 * Wire format on the SSE stream:
 *   id:    <ring seq>
 *   event: <type>            (e.g. "activity.updated")
 *   data:  <JSON of the full WikiSseEvent, including `type`>
 *
 * Payload shapes deliberately mirror the vendored store item shapes
 * (core.ts) — the bridge forwards store state verbatim, and the
 * vendor-sync store-surface tripwire (graft G-8) guards against silent
 * upstream drift.
 */
import { z } from "zod";
import { ActivityItemSchema, DedupTaskSchema, FileChangeTaskSchema, FileNodeSchema, IngestTaskSchema, IsoDateTimeSchema, JsonValueSchema, LintItemSchema, MessageReferenceSchema, ResearchTaskSchema, ReviewItemSchema, UuidSchema, } from "./core.js";
import { JobEventTypeSchema, JobKindSchema, JobStatusSchema } from "./jobs.js";
// ─── activity.* (activity-store bridge) ──────────────────────────────────
export const ActivityAddedEventSchema = z.object({
    type: z.literal("activity.added"),
    payload: z.object({ item: ActivityItemSchema }),
});
export const ActivityUpdatedEventSchema = z.object({
    type: z.literal("activity.updated"),
    /** Full post-update item (covers updateItem + appendDetail). */
    payload: z.object({ item: ActivityItemSchema }),
});
export const ActivityClearedEventSchema = z.object({
    type: z.literal("activity.cleared"),
    /** Ids that remain after clearDone(). */
    payload: z.object({ remainingIds: z.array(z.string()) }),
});
// ─── queue.updated (ingest + dedup queue mirror, F-026/F-043) ────────────
export const QueueSnapshotPayloadSchema = z.union([
    z.object({
        queue: z.literal("ingest"),
        tasks: z.array(IngestTaskSchema),
        processing: z.boolean(),
    }),
    z.object({
        queue: z.literal("dedup"),
        tasks: z.array(DedupTaskSchema),
        processing: z.boolean(),
    }),
]);
export const QueueUpdatedEventSchema = z.object({
    type: z.literal("queue.updated"),
    payload: QueueSnapshotPayloadSchema,
});
// ─── file-sync.* (rustd watcher events, file_sync.rs) ────────────────────
/** Mirrors desktop `file-sync://queue-updated` `{projectId, tasks}`. */
export const FileSyncQueueUpdatedEventSchema = z.object({
    type: z.literal("file-sync.queue-updated"),
    payload: z.object({
        projectId: z.string(),
        tasks: z.array(FileChangeTaskSchema),
    }),
});
/** Mirrors desktop `file-sync://changed` `{projectId, tasks}`. */
export const FileSyncChangedEventSchema = z.object({
    type: z.literal("file-sync.changed"),
    payload: z.object({
        projectId: z.string(),
        tasks: z.array(FileChangeTaskSchema),
    }),
});
/** Watcher lifecycle (server extension: N-project watcher + G-6 mode). */
export const FileSyncWatcherStatusEventSchema = z.object({
    type: z.literal("file-sync.watcher-status"),
    payload: z.object({
        projectId: z.string(),
        status: z.enum(["started", "stopped", "rescan-scheduled", "error"]),
        mode: z.enum(["watch", "scheduled-rescan"]),
        error: z.string().optional(),
    }),
});
// ─── data-version / file-tree (wiki-store bridge) ────────────────────────
/** `bumpDataVersion()` → clients re-fetch invalidated reads. */
export const DataVersionEventSchema = z.object({
    type: z.literal("data-version"),
    payload: z.object({ version: z.number().int().nonnegative() }),
});
/** `setFileTree()` → full project tree push (engine FileNode shape). */
export const FileTreeEventSchema = z.object({
    type: z.literal("file-tree"),
    payload: z.object({ tree: z.array(FileNodeSchema) }),
});
// ─── research.* (research-store bridge, F-141/142) ───────────────────────
export const ResearchUpdatedEventSchema = z.object({
    type: z.literal("research.updated"),
    /** Full post-update task (add + update + progressive synthesis). */
    payload: z.object({ task: ResearchTaskSchema }),
});
export const ResearchRemovedEventSchema = z.object({
    type: z.literal("research.removed"),
    payload: z.object({ id: z.string() }),
});
// ─── review / lint (review-store, lint-store bridges) ────────────────────
export const ReviewUpdatedEventSchema = z.object({
    type: z.literal("review.updated"),
    payload: z.object({ items: z.array(ReviewItemSchema) }),
});
export const LintUpdatedEventSchema = z.object({
    type: z.literal("lint.updated"),
    payload: z.object({ items: z.array(LintItemSchema) }),
});
// ─── chat.* streaming (chat orchestrator, F-024/F-198/F-262) ─────────────
export const ChatTokenEventSchema = z.object({
    type: z.literal("chat.token"),
    payload: z.object({
        streamId: z.string(),
        conversationId: z.string().optional(),
        token: z.string(),
    }),
});
export const ChatReasoningEventSchema = z.object({
    type: z.literal("chat.reasoning"),
    payload: z.object({
        streamId: z.string(),
        conversationId: z.string().optional(),
        text: z.string(),
    }),
});
export const ChatReferencesEventSchema = z.object({
    type: z.literal("chat.references"),
    payload: z.object({
        streamId: z.string(),
        conversationId: z.string().optional(),
        references: z.array(MessageReferenceSchema),
    }),
});
export const ChatDoneEventSchema = z.object({
    type: z.literal("chat.done"),
    payload: z.object({
        streamId: z.string(),
        conversationId: z.string().optional(),
        content: z.string(),
        references: z.array(MessageReferenceSchema).optional(),
    }),
});
export const ChatErrorEventSchema = z.object({
    type: z.literal("chat.error"),
    payload: z.object({
        streamId: z.string(),
        conversationId: z.string().optional(),
        error: z.string(),
    }),
});
// ─── job.* (jobs mirror + durable job_events live tail, G-7) ─────────────
export const JobUpdatedEventSchema = z.object({
    type: z.literal("job.updated"),
    payload: z.object({
        jobId: UuidSchema,
        kind: JobKindSchema,
        status: JobStatusSchema,
        summary: JsonValueSchema,
    }),
});
/** Live mirror of a `job_events` row (durable copy is in Postgres). */
export const JobEventEventSchema = z.object({
    type: z.literal("job.event"),
    payload: z.object({
        jobId: UuidSchema,
        seq: z.number().int().positive(),
        ts: IsoDateTimeSchema,
        eventType: JobEventTypeSchema,
        data: JsonValueSchema,
    }),
});
// ─── The union ───────────────────────────────────────────────────────────
export const WikiSseEventSchema = z.discriminatedUnion("type", [
    ActivityAddedEventSchema,
    ActivityUpdatedEventSchema,
    ActivityClearedEventSchema,
    QueueUpdatedEventSchema,
    FileSyncQueueUpdatedEventSchema,
    FileSyncChangedEventSchema,
    FileSyncWatcherStatusEventSchema,
    DataVersionEventSchema,
    FileTreeEventSchema,
    ResearchUpdatedEventSchema,
    ResearchRemovedEventSchema,
    ReviewUpdatedEventSchema,
    LintUpdatedEventSchema,
    ChatTokenEventSchema,
    ChatReasoningEventSchema,
    ChatReferencesEventSchema,
    ChatDoneEventSchema,
    ChatErrorEventSchema,
    JobUpdatedEventSchema,
    JobEventEventSchema,
]);
export const WIKI_SSE_EVENT_TYPES = WikiSseEventSchema.options.map((o) => o.shape.type.value);
// ─── events ring row (§8.4 `events` table) ───────────────────────────────
/**
 * Postgres NOTIFY channel that announces "the events_ring advanced for
 * project X" — payload `{projectId, lastSeq}`. BOTH producers (gateway's
 * own appends AND engined's uplink persistence) must notify this one
 * channel; the gateway's events hub LISTENs on it to wake SSE
 * subscribers. Single source of truth here ([G-14]) — a name drift
 * between producer and listener silently kills live event fan-out.
 */
export const EVENTS_RING_NOTIFY_CHANNEL = "wiki_events";
export const EventRingRowSchema = z.object({
    /** bigserial — becomes the SSE `id:` field / `Last-Event-ID` cursor. */
    seq: z.number().int().positive(),
    projectId: UuidSchema,
    type: z.string(),
    payload: JsonValueSchema,
    at: IsoDateTimeSchema,
});
/**
 * A project-stamped event as it travels runtime → engined → gateway
 * (before the ring assigns `seq`). The bridge emits these.
 */
export const ProjectEventSchema = z.object({
    projectId: UuidSchema,
    event: WikiSseEventSchema,
    /** Runtime-local monotonic counter; used for intra-batch ordering. */
    localSeq: z.number().int().nonnegative(),
    atMs: z.number().int().nonnegative(),
});
//# sourceMappingURL=events.js.map